import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Indicator,
  Menu,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  FiBell,
  FiChevronDown,
  FiChevronLeft,
  FiHome,
  FiLogOut,
  FiMenu,
  FiUser,
  FiSettings,
} from "react-icons/fi";
import { logout } from "../../features/auth/authActions";
import { resetStep, setFormData } from "../../features/formData/formSlice";
import Api from "../components/api/Api";
import {
  ADMIN_PANEL_HOME,
  getAdminPageTitle,
  getAuthPayload,
  getAuthUser,
  getUserDisplayName,
  getUserTypeName,
} from "./adminNavigation";
import { useState, useEffect } from "react";

const AdminTopbar = ({
  titleGetter = getAdminPageTitle,
  homePath = ADMIN_PANEL_HOME,
  logoutRedirect = "/admin-login",
  collapsed = false,
  isMobile = false,
  onOpenDrawer,
  onToggleCollapse,
}) => {
  const authState = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = getAuthUser(authState);
  const authPayload = getAuthPayload(authState);
  const currentUserId = authUser?.id || authPayload?.id || authState?.user?.id || authState?.user?.user?.id;

  const [fetchedDetail, setFetchedDetail] = useState(null);

  const getDetailsApi = Api({
    url: currentUserId ? `admin/user/id/${currentUserId}` : "",
  });

  useEffect(() => {
    if (currentUserId) {
      getDetailsApi
        .fetchData()
        .then((res) => {
          if (res?.data) {
            setFetchedDetail(res.data);
          }
        })
        .catch((err) => console.error("Error fetching user topbar detail:", err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  const pageTitle = titleGetter(location.pathname);
  const displayName = getUserDisplayName(fetchedDetail ? { user: fetchedDetail } : authState);
  const emailAddress =
    fetchedDetail?.user?.email_address ||
    fetchedDetail?.user?.email_id ||
    authUser?.email_address ||
    authUser?.email ||
    authUser?.email_id ||
    "";
  const userType = getUserTypeName(authState) ?? "ADMIN";

  const userPhoto =
    fetchedDetail?.user?.photo ||
    fetchedDetail?.user?.image_url ||
    fetchedDetail?.user?.profile_photo ||
    fetchedDetail?.customerDetail?.photo ||
    fetchedDetail?.customerDetail?.image_url ||
    fetchedDetail?.customerDetail?.profile_photo ||
    fetchedDetail?.photo ||
    fetchedDetail?.image_url ||
    fetchedDetail?.profile_photo ||
    authUser?.photo ||
    authUser?.image_url ||
    authUser?.profile_photo ||
    authUser?.image ||
    authUser?.profile_image ||
    authUser?.avatar ||
    authPayload?.photo ||
    authPayload?.image_url ||
    authPayload?.profile_photo ||
    authPayload?.image ||
    authUser?.customerDetails?.photo ||
    authUser?.customerDetails?.image_url ||
    authUser?.customerDetails?.profile_photo ||
    authUser?.customerDetails?.image ||
    authUser?.customer?.photo ||
    authUser?.customer?.image_url ||
    authUser?.customer?.profile_photo ||
    authUser?.userDetails?.photo ||
    authUser?.userDetails?.image_url ||
    authUser?.userDetails?.profile_photo ||
    authUser?.user?.photo ||
    authUser?.user?.image_url ||
    authUser?.user?.profile_photo;

  const rawAssetUrl =
    fetchedDetail?.assetUrl ||
    authPayload?.assetUrl ||
    authState?.user?.assetUrl ||
    authState?.assetUrl ||
    process.env.REACT_APP_ASSET_URL ||
    "https://images.allhomedesire.in";
  const cleanAssetUrl = rawAssetUrl.replace(/\/+$/, "");

  const fullAvatarSrc = userPhoto
    ? userPhoto.startsWith("http://") || userPhoto.startsWith("https://")
      ? userPhoto
      : `${cleanAssetUrl}${userPhoto.startsWith("/") ? "" : "/"}${userPhoto}`
    : null;

  const initials = useMemo(
    () =>
      displayName
        .split(" ")
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(""),
    [displayName]
  );

  const handleLogout = () => {
    dispatch(resetStep());
    dispatch(setFormData({}));
    dispatch(logout());
    navigate(logoutRedirect, { replace: true });
  };

  return (
    <header className="admin-topbar">
      <div className="admin-topbar__inner">
        <div className="admin-topbar__main">
          <button
            type="button"
            className="admin-topbar__shell-button"
            onClick={isMobile ? onOpenDrawer : onToggleCollapse}
            aria-label={
              isMobile
                ? "Open navigation"
                : collapsed
                  ? "Expand navigation"
                  : "Collapse navigation"
            }
          >
            {isMobile ? (
              <FiMenu size={18} />
            ) : (
              <FiChevronLeft
                size={18}
                style={{
                  transform: collapsed ? "rotate(180deg)" : "none",
                  transition: "transform 0.18s ease",
                }}
              />
            )}
          </button>

          <div className="admin-topbar__copy">
            <h1 className="admin-topbar__title">{pageTitle}</h1>
          </div>
        </div>

        <div className="admin-topbar__actions">
          <Link to="/notification" className="admin-topbar__icon-link">
            <Indicator
              inline
              size={10}
              color="red"
              disabled={location.pathname === "/notification"}
              offset={3}
            >
              <FiBell size={18} />
            </Indicator>
          </Link>

          <Menu shadow="lg" width={260} position="bottom-end" withArrow radius="md" transitionProps={{ transition: 'pop-top-right' }}>
            <Menu.Target>
              <UnstyledButton className="admin-topbar__profile">
                <Avatar src={fullAvatarSrc} radius="xl" size={38} color="blue">
                  {initials || "AD"}
                </Avatar>

                <div className="admin-topbar__profile-copy">
                  <Text className="admin-topbar__profile-name">{displayName}</Text>
                  {emailAddress && (
                    <Text className="admin-topbar__profile-role" style={{ textTransform: 'none' }}>
                      {emailAddress}
                    </Text>
                  )}
                </div>

                <FiChevronDown size={16} className="admin-topbar__profile-caret" />
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown style={{ padding: "8px" }}>
              <Menu.Label style={{ textTransform: "capitalize", fontSize: "12px", fontWeight: 700, color: "#111827", paddingBottom: "8px" }}>
                {userType.replaceAll("_", " ").toLowerCase()}
              </Menu.Label>
              <Menu.Divider />

              <Menu.Item
                leftSection={<FiHome size={15} />}
                component={Link}
                to={homePath}
                rightSection={<span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 500 }}>⌘H</span>}
                style={{ marginTop: "4px" }}
              >
                Workspace
              </Menu.Item>

              <Menu.Item
                leftSection={<FiUser size={15} />}
                component={Link}
                to="/user/profile"
                rightSection={<span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 500 }}>⌘P</span>}
                style={{ marginTop: "2px" }}
              >
                Profile
              </Menu.Item>

              <Menu.Item
                leftSection={<FiSettings size={15} />}
                component={Link}
                to="/my-preference"
                rightSection={<span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 500 }}>⌘S</span>}
                style={{ marginTop: "2px", marginBottom: "4px" }}
              >
                Settings
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item
                color="red"
                leftSection={<FiLogOut size={15} />}
                onClick={handleLogout}
                style={{ marginTop: "4px" }}
              >
                Log out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default AdminTopbar;
